import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import React, { useEffect, useRef,  useState } from 'react';

import { useKeyboard } from '$hooks/events';

import { Button } from '$partials/ui';
import { IconClose } from '$partials/icons';
import { Collapsible, CollapsibleContent, CollapsibleContextConsumer, CollapsibleHeading } from '$partials/base/collapsible';
import IconChevronUp from '$partials/icons/icon-chevron-up';
import IconChevronDown from '$partials/icons/icon-chevron-down';
import IconArrowRight from '$partials/icons/icon-arrow-right';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const AffiliatesFilterDialog = ({
    isOpen,
    onClose,
    clearAllFilters,
    hasFilters,
    organisationFocusFilter,
}) => {
    const [animationOut, setAnimationOut] = useState(false);
    const dialogRef = useRef();

    const handleClose = () => {

        setAnimationOut(true);

        setTimeout(() => {
            onClose();
        }, 250);
    };

    useKeyboard({
        'Escape': () => isOpen && handleClose()
    }, [isOpen]);

    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        if (isOpen) {
            if (dialogNode && !dialogNode.open) {
                disableBodyScroll(dialogNode);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open) {
                enableBodyScroll(dialogNode);
                dialogNode.close?.();
                setAnimationOut(false);
            }
        }
        return () => {
            enableBodyScroll(dialogNode);
        };
    }, [isOpen]);

    return (
        <dialog
            className={`dialog-reset backdrop-blur-in ${animationOut ? 'blur-out' : ''}`}
            onClick={handleClose}
            ref={dialogRef}>
            <div
                className={`w-full overflow-x-scroll fixed top-0 bottom-0 left-0 min-h-screen slide-x-fade-in flex flex-col  ${animationOut ? 'slide-out' : ''}`}>

                {/* Container */}
                <div
                    className='layout-container-padding-right flex flex-wrap max-w-container pt-60 sm:pt-5 sm:pl-5 flex-1 -mr-10 sm:mr-0'>
                    {/* Left Sidebar */}
                    <div
                        className='w-sidebar bg-white rounded-ss-md'
                        onClick={(e) => e.stopPropagation()}>
                        <div className='flex flex-col items-center border-e border-black/10 h-full'>
                            {/* Empty */}
                        </div>
                    </div>

                    {/* Main content */}
                    <div className='flex-1 flex flex-wrap layout-negative-margin'>
                        <div
                            className='w-full sm:w-9/12 layout-item-padding bg-white rounded-se-md'
                            onClick={(e) => e.stopPropagation()}>
                            {/* Close buttons */}
                            <Button
                                className='ml-auto block rounded-full bg-grey p-12 mt-15 mr-20 sm:mr-55 sm:mt-55'
                                label='Close modal'
                                look='icon'
                                onClick={handleClose}
                                renderIcon={<IconClose color='black' />}>
                            </Button>

                            <div className='pr-20 sm:pr-55'>
                                <p className='type-style-4'>
                                    Filters
                                </p>

                                <div className='flex flex-col gap-40 mt-40'>
                                    {/* Category */}
                                    {organisationFocusFilter?.options?.length > 0 && (
                                        <MultiSelectAccordion
                                            id='categories'
                                            label='Category'
                                            {...organisationFocusFilter} />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Apply and reset */}
                <div
                    className='layout-container-padding-right flex flex-wrap max-w-container sm:pl-5 sm:pb-5 -mr-10 sm:mr-0'>
                    {/* Left Sidebar */}
                    <div
                        className='w-sidebar bg-white rounded-es-md border-t border-black/10'
                        onClick={(e) => e.stopPropagation()}>
                        <div className='flex flex-col items-center'>
                            {/* Empty */}
                        </div>
                    </div>

                    {/* Main content */}
                    <div className='flex-1 flex flex-wrap layout-negative-margin'>
                        {/* Column 1 */}
                        <div
                            className='w-full sm:w-9/12 layout-item-padding bg-white rounded-ee-md border-t border-black/10 '
                            onClick={(e) => e.stopPropagation()}>
                            <div className='inline-flex flex-wrap items-center py-30'>
                                {/* Apply (close) */}
                                <button
                                    className={'bg-grey type-style-7 self-start cursor-pointer select-none inline-flex items-center py-8 px-12 rounded-full'}
                                    onClick={handleClose}
                                    type='button'>
                                    {'Apply'}
                                    <div
                                        aria-hidden={true}
                                        className='-mb-1 pl-8'>
                                        <IconArrowRight />
                                    </div>
                                </button>

                                {/* Clear button */}
                                {hasFilters && (
                                    <button
                                        className={'type-style-7 self-start cursor-pointer select-none inline-flex items-center py-8 px-12'}
                                        onClick={clearAllFilters}
                                        type='button'>
                                        {'Clear'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </dialog>
    );
};


const MultiSelectAccordion = ({
    children,
    id,
    label,
    options,
    toggleValue,
    value,
}) => {
    return (
        <Collapsible
            className='w-full'
            id={id}
            isOpened={false}>
            <CollapsibleHeading className='w-full p-0'>
                <CollapsibleContextConsumer>
                    {({ isOpened }) => (
                        <div className='flex justify-between'>
                            <h6 className='type-style-4'>
                                {label}
                            </h6>
                            <div
                                aria-hidden='true'
                                className='block rounded-full bg-grey px-12 py-8'>
                                <div className='-mb-1'>
                                    {isOpened ? <IconChevronUp /> : <IconChevronDown />}
                                </div>
                            </div>
                        </div>
                    )}
                </CollapsibleContextConsumer>
            </CollapsibleHeading>
            <CollapsibleContent className='clearfix mt-30'>
                <div className='inline-flex flex-wrap gap-10'>
                    {options.map((option) => {
                        const selected = value.includes(option.value);
                        return (
                            <button
                                className={`type-style-7 self-start cursor-pointer select-none inline-flex items-center py-8 px-12 rounded-full border ${selected ? 'bg-black text-white border-black' : 'border-black/25 bg-grey'}`}
                                key={option.value}
                                onClick={toggleValue.bind(null, option.value)}
                                type='button'>
                                {option.label}
                                <div
                                    aria-hidden={true}
                                    className='-mb-1 pl-8'>
                                    {selected && (
                                        <IconClose fill='#ffffff'/>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                    {children}
                </div>
            </CollapsibleContent>
        </Collapsible>
    );
};

MultiSelectAccordion.propTypes = {
    children: PropTypes.object,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
    })),
    toggleValue: PropTypes.func,
    value: PropTypes.array,
};



const MultiSelectAccordionSection = ({
    id,
    label,
    options,
    toggleValue,
    value,
}) => {

    return (
        <div
            className='mt-30'
            id={id}>
            <p
                className='mb-18 type-style-6'
                style={{ fontWeight: '600'}}>
                {label}
            </p>
            <div className='inline-flex flex-wrap gap-10'>
                {options.map((option) => {
                    const selected = value.includes(option.value);
                    return (
                        <button
                            className={`type-style-7 self-start cursor-pointer select-none inline-flex items-center py-8 px-12 rounded-full border ${selected ? 'bg-black text-white border-black' : 'border-black/25'}`}
                            key={option.value}
                            onClick={toggleValue.bind(null, option.value)}
                            type='button'>
                            {option.label}
                            <div
                                aria-hidden={true}
                                className='-mb-1 pl-8'>
                                {selected && (
                                    <IconClose fill='#ffffff'/>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

MultiSelectAccordionSection.propTypes = {
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
    })),
    toggleValue: PropTypes.func,
    value: PropTypes.array,
};


/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */


const multiFilterObject = () => PropTypes.shape({
    options: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        value: PropTypes.string,
    })),
    toggleValue: PropTypes.func,
    value: PropTypes.array,
});

const propTypes = {
    onClose: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
    hasFilters: PropTypes.bool.isRequired,
    clearAllFilters: PropTypes.func.isRequired,
    organisationFocusFilter: multiFilterObject(),
};

AffiliatesFilterDialog.displayName = 'AffiliatesFilterDialog';
AffiliatesFilterDialog.propTypes = propTypes;

export { AffiliatesFilterDialog };
export default AffiliatesFilterDialog;
